import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, Loader2 } from "lucide-react";

const inr = (paise) => paise == null ? "—"
  : "₹" + (Number(paise) / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 });

/**
 * Contractors on a lead split trades — civil, electrical, plumbing — rather
 * than competing for the same job. So this is "add another contractor for a
 * different part of the work", and every selection carries a scope.
 */
export default function AssignModal({ lead, partners, onClose, onAssigned }) {
  const [existing, setExisting] = useState(null);   // null = still loading
  const [stages, setStages] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [selected, setSelected] = useState({});      // partner_id -> {scope_label, scope_stage_ids}
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Both requests race in parallel. allSettled + a `cancelled` guard collapses
    // them to a single failure path: at most one alert, at most one onClose(),
    // and no setState after this modal has already unmounted (e.g. the sibling
    // request's own failure already closed it first).
    let cancelled = false;
    Promise.allSettled([
      api.get(`/admin/leads/${lead.id}/assignments`),
      api.get("/construction-stages"),
    ]).then(([assignmentsResult, stagesResult]) => {
      if (cancelled) return;
      if (assignmentsResult.status === "fulfilled") setExisting(assignmentsResult.value.data);
      if (stagesResult.status === "fulfilled") setStages(stagesResult.value.data);
      const failed = [assignmentsResult, stagesResult].find(r => r.status === "rejected");
      if (failed) {
        const err = failed.reason;
        window.alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        onClose();
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id]);

  const assignedIds = new Set((existing || []).map(a => a.partner_id));
  const needsConfirm = existing !== null && existing.length > 0 && !confirmed;

  const toggle = (p) => setSelected(prev => {
    const next = { ...prev };
    if (next[p.id]) { delete next[p.id]; return next; }
    next[p.id] = {
      // Default the scope label from what the partner does — admin can edit it.
      scope_label: (p.professions || []).slice(0, 2).join(" / ") || "",
      scope_stage_ids: [],
    };
    return next;
  });

  const submit = async () => {
    setSaving(true);
    try {
      const contractors = Object.entries(selected).map(([partner_id, v]) => ({
        partner_id,
        scope_label: v.scope_label || null,
        scope_stage_ids: v.scope_stage_ids,
      }));
      const { data } = await api.post("/admin/leads/assign", { lead_id: lead.id, contractors });
      const lines = [];
      if (data.assigned.length) lines.push(`Assigned: ${data.assigned.map(a => a.name || a.partner_id).join(", ")}`);
      if (data.already_assigned.length) lines.push(`Already on this lead: ${data.already_assigned.map(a => a.name || a.partner_id).join(", ")}`);
      data.rejected.forEach(r => lines.push(`Not assigned — ${r.name || r.partner_id}: ${r.reason}`));
      if (lines.length) window.alert(lines.join("\n"));
      onAssigned();
      onClose();
    } catch (err) {
      window.alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="assign-modal">
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-semibold text-slate-900">
              {needsConfirm ? "Contractors on this lead" : "Add contractor"}
            </h3>
            <p className="text-sm text-slate-500">{lead.name} • {lead.mobile}</p>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        {existing === null && <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}

        {needsConfirm && (
          <div data-testid="assign-confirm-step">
            <div className="space-y-2">
              {existing.map(a => (
                <div key={a.id} className="border border-slate-200 rounded-xl p-3">
                  <div className="font-medium text-slate-900">{a.partner_name}</div>
                  <div className="text-sm text-slate-600">{a.scope_label || "Scope not set"}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    {a.status}
                    {a.current_stage_name ? ` · ${a.current_stage_name}` : ""}
                    {a.percent_complete != null ? ` · ${a.percent_complete}%` : ""}
                    {a.amount_paid_paise != null ? ` · ${inr(a.amount_paid_paise)} received` : ""}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-slate-700">
              Add another contractor for a different part of the work?
            </p>
            <div className="mt-4 flex gap-2">
              <button onClick={onClose} className="nc-btn-secondary py-2">Cancel</button>
              <button onClick={() => setConfirmed(true)} className="nc-btn-primary py-2"
                      data-testid="assign-add-another-btn">Add contractor</button>
            </div>
          </div>
        )}

        {existing !== null && !needsConfirm && (
          <div data-testid="assign-picker-step">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">
              Select contractors and set what each is responsible for
            </p>
            <div className="space-y-2 max-h-[45vh] overflow-y-auto">
              {partners.map(p => {
                const already = assignedIds.has(p.id);
                const picked = !!selected[p.id];
                return (
                  <div key={p.id}
                       className={`p-3 rounded-xl border ${already ? "border-slate-200 bg-slate-50 opacity-60" : "border-slate-200"}`}>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" disabled={already} checked={picked}
                             onChange={() => toggle(p)} data-testid={`select-partner-${p.id}`} />
                      <div className="flex-1">
                        <div className="font-medium text-slate-900 text-sm">
                          {p.name}
                          {already && <span className="ml-2 text-xs text-slate-500">Already assigned</span>}
                        </div>
                        <div className="text-xs text-slate-500">
                          {p.state} • {p.district} • {p.professions?.join(", ")}
                        </div>
                      </div>
                    </label>

                    {picked && (
                      <div className="mt-3 pl-7 space-y-2">
                        <label className="flex flex-col">
                          <span className="text-xs text-slate-500">Scope label</span>
                          <input className="nc-input py-2" placeholder="Civil / Structure"
                                 value={selected[p.id].scope_label}
                                 onChange={e => setSelected(s => ({ ...s, [p.id]: { ...s[p.id], scope_label: e.target.value } }))}
                                 data-testid={`scope-label-${p.id}`} />
                        </label>
                        <div>
                          <span className="text-xs text-slate-500">
                            Stages they own — leave all unticked for a full-build contractor
                          </span>
                          <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-1 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2">
                            {stages.map(st => (
                              <label key={st.id} className="flex items-center gap-2 text-sm text-slate-700">
                                <input type="checkbox"
                                       checked={selected[p.id].scope_stage_ids.includes(st.id)}
                                       onChange={e => setSelected(s => {
                                         const ids = new Set(s[p.id].scope_stage_ids);
                                         if (e.target.checked) ids.add(st.id); else ids.delete(st.id);
                                         return { ...s, [p.id]: { ...s[p.id], scope_stage_ids: [...ids] } };
                                       })}
                                       data-testid={`scope-stage-${p.id}-${st.id}`} />
                                {st.name}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {partners.length === 0 && (
                <p className="text-sm text-slate-400 text-center p-4">No approved partners yet.</p>
              )}
            </div>
            <button onClick={submit} disabled={!Object.keys(selected).length || saving}
                    className="mt-4 nc-btn-primary w-full justify-center"
                    data-testid="assign-confirm-btn">
              {saving ? "Assigning…" : `Assign ${Object.keys(selected).length} contractor(s)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
